import { useEffect } from "react";

import { gql } from "@apollo/client";
import Select from "react-select";

import { useClientWorkspaceContext } from "@/components/pages_logged_in/workspace/clients/ClientWorkspaceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetAllClientsQuery } from "@/graphql";
import { SelectFn, SelectOption } from "@/types/select";

export const GET_ALL_CLIENTS_QUERY = gql`
  query GetAllClients($coachId: ID) {
    clients(coachId: $coachId) {
      id
      name
      email
    }
  }
`;

interface ClientSelectorProps {
  coachId: string;
  selectedClientId: string | null;
  selectedWorkspaceId: string | null;
  onSelectClient: (clientId: string) => void;
  onSelectWorkspace: (workspaceId: string) => void;
}

export function ClientSelector({
  coachId,
  selectedClientId,
  selectedWorkspaceId,
  onSelectClient,
  onSelectWorkspace,
}: ClientSelectorProps) {
  const { data, loading } = useGetAllClientsQuery({
    variables: {
      coachId: coachId,
    },
  });
  const clients = data?.clients || [];

  // clientId from the parent component
  useEffect(() => {
    if (!selectedClientId && clients.length > 0) {
      onSelectClient(clients[0].id);
    }
  }, [selectedClientId, clients]);

  const clientOptions: SelectOption[] = clients.map((c) => ({
    value: c.id,
    label:
      c.name && c.email
        ? `${c.name} (${c.email})`
        : c.name || c.email || "Unknown",
  }));

  const onSelectClientOption: SelectFn<SelectOption> = (option) => {
    onSelectClient(option?.value || "");
  };

  const { workspaces } = useClientWorkspaceContext();
  const workspaceOptions: SelectOption[] = workspaces.map((w) => ({
    value: w.id,
    label: w.name,
  }));

  const onSelectWorkspaceOption: SelectFn<SelectOption> = (option) => {
    onSelectWorkspace(option?.value || "");
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Schedule Session For</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col gap-2">
          <Select
            value={clientOptions.find((o) => o.value === selectedClientId)}
            onChange={onSelectClientOption}
            isLoading={loading}
            isSearchable={true}
            options={clientOptions}
            className="font-normal"
          />
        </div>
        <div>
          <div className="text-md font-semibold mb-4">In Workspace</div>
          <div className="flex flex-col gap-2">
            <Select
              value={workspaceOptions.find(
                (o) => o.value === selectedWorkspaceId,
              )}
              onChange={onSelectWorkspaceOption}
              isLoading={loading}
              isSearchable={true}
              options={workspaceOptions}
              className="font-normal"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
